import type { SortDescriptor } from "react-stately";

import { useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import type { BadgeVariant } from "@/components/badge";

import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";
import { BulkChangeStatusModal } from "@/features/requests/components/modals/BulkChangeStatusModal";
import {
  useBulkChangeStatus,
  useBulkCopyLinks,
} from "@/features/requests/hooks";
import { STATUS_OPTIONS } from "@/features/shared/constants/statusColors";
import { useDebounce } from "@/hooks/useDebounce";

import type { DropdownFilterType, ToggleFilterType } from "./types";

import { ProjectRequestsBulkActions } from "./components/ProjectRequestsBulkActions";
import { ProjectRequestsFilters } from "./components/ProjectRequestsFilters";
import { ProjectRequestsStatistics } from "./components/ProjectRequestsStatistics";
import { ProjectRequestsTable } from "./components/ProjectRequestsTable";
import { projectRequestsTableConfig } from "./config/tableConfig";
import { useProjectRequests } from "./hooks/useProjectRequests";

const getRequestIdFromTableRowId = (tableRowId: string): string => {
  const parts = tableRowId.split("-");

  return parts.at(-1) ?? "";
};

export default function ProjectRequests() {
  const { projectId } = useParams({
    from: "/_auth/_app-shell/project-associated-requests/$projectId",
  });
  const numericProjectId = Number(projectId);
  const { hasRole } = useRoleAccess();
  const isDesigner = hasRole(["designer"]);

  const [dropdownFilter, setDropdownFilter] =
    useState<DropdownFilterType>("open");
  const [toggleFilter, setToggleFilter] = useState<ToggleFilterType>("all");
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounce(searchValue, 300);

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [cachedTotalItems, setCachedTotalItems] = useState<null | number>(null);
  const [cachedTotalPages, setCachedTotalPages] = useState<null | number>(null);
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: projectRequestsTableConfig.defaultSort?.column || "createdAt",
    direction:
      projectRequestsTableConfig.defaultSort?.direction === "desc"
        ? "descending"
        : "ascending",
  });

  const [selectedItems, setSelectedItems] = useState<Set<number | string>>(
    new Set(),
  );

  const [pendingStatusChange, setPendingStatusChange] = useState<{
    selectedRequests: Array<{ id: number; name: string }>;
    statusId: number;
    statusLabel: string;
    statusVariant: BadgeVariant;
  } | null>(null);

  const isQueuedFilter = dropdownFilter === "queued";

  const {
    data: requestsData,
    isError,
    isLoading,
    refetch,
  } = useProjectRequests({
    dropdownFilter,
    limit: isQueuedFilter ? undefined : itemsPerPage,
    page: isQueuedFilter ? undefined : page,
    projectId: numericProjectId,
    search: debouncedSearch || undefined,
    sortDescriptor: isQueuedFilter ? undefined : sortDescriptor,
    toggleFilter,
  });

  useEffect(() => {
    if (page === 1) {
      if (requestsData?.total != null) setCachedTotalItems(requestsData.total);
      if (requestsData?.pagination?.totalPages)
        setCachedTotalPages(requestsData.pagination.totalPages);
    }
  }, [page, requestsData?.total, requestsData?.pagination?.totalPages]);

  useEffect(() => {
    setCachedTotalItems(null);
    setCachedTotalPages(null);
  }, [dropdownFilter, toggleFilter, debouncedSearch]);

  const bulkCopyLinksMutation = useBulkCopyLinks({
    onSuccess: () => setSelectedItems(new Set()),
  });

  const bulkChangeStatusMutation = useBulkChangeStatus({
    onSuccess: () => {
      setSelectedItems(new Set());
      setPendingStatusChange(null);
    },
  });

  const getSelectedRequestIds = useCallback(
    () =>
      [...selectedItems]
        .map((id) => getRequestIdFromTableRowId(String(id)))
        .map(Number)
        .filter((id) => !Number.isNaN(id) && id > 0),
    [selectedItems],
  );

  const getSelectedRequests = useCallback(() => {
    const selectedIds = getSelectedRequestIds();
    const requests = requestsData?.requests || [];

    return selectedIds
      .map((id) => {
        const request = requests.find((r) => r.id === id);

        return request ? { id, name: request.name } : null;
      })
      .filter((r): r is { id: number; name: string } => r !== null);
  }, [getSelectedRequestIds, requestsData?.requests]);

  const handleDropdownChange = (filter: DropdownFilterType) => {
    setDropdownFilter(filter);
    setSelectedItems(new Set());
    setPage(1);
  };

  const handleToggleChange = (filter: ToggleFilterType) => {
    setToggleFilter(filter);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setPage(1);
  };

  const handleSortChange = (newSortDescriptor: SortDescriptor) => {
    setSortDescriptor(newSortDescriptor);
  };

  const handleSelectionChange = (items: Set<number | string>) => {
    setSelectedItems(items);
  };

  const handleClearSelection = () => {
    setSelectedItems(new Set());
  };

  const handleSelectAll = useCallback(() => {
    const allCurrentPageIds = (requestsData?.requests || []).map(
      (request) => `${projectRequestsTableConfig.id}-${request.id}`,
    );
    const areAllSelected = allCurrentPageIds.every((id) =>
      selectedItems.has(id),
    );

    setSelectedItems(areAllSelected ? new Set() : new Set(allCurrentPageIds));
  }, [requestsData?.requests, selectedItems]);

  const handleBulkCopyLinks = useCallback(() => {
    const selectedIds = getSelectedRequestIds();

    if (selectedIds.length === 0) return;
    bulkCopyLinksMutation.mutate(selectedIds);
  }, [getSelectedRequestIds, bulkCopyLinksMutation]);

  const handleStatusSelect = useCallback(
    (statusId: number, statusLabel: string) => {
      const statusOption = STATUS_OPTIONS.find((s) => s.id === statusId);
      const statusVariant = statusOption?.variant ?? "primary";
      const selectedRequests = getSelectedRequests();

      setPendingStatusChange({
        selectedRequests,
        statusId,
        statusLabel,
        statusVariant,
      });
    },
    [getSelectedRequests],
  );

  const confirmStatusChange = useCallback(() => {
    if (!pendingStatusChange) return;

    const selectedIds = getSelectedRequestIds();

    if (selectedIds.length === 0) return;

    bulkChangeStatusMutation.mutate({
      requestIds: selectedIds,
      statusId: pendingStatusChange.statusId,
    });
  }, [getSelectedRequestIds, pendingStatusChange, bulkChangeStatusMutation]);

  const isBulkLoading =
    bulkCopyLinksMutation.isPending || bulkChangeStatusMutation.isPending;
  const selectedCount = selectedItems.size;

  return (
    <div className="py-5 px-4 lg:px-5 space-y-4">
      <div className="flex flex-col gap-4 border-b border-b-border-secondary pb-3 lg:grid lg:grid-cols-5 lg:items-start lg:gap-6">
        <ProjectRequestsFilters
          dropdownFilter={dropdownFilter}
          isDesigner={isDesigner}
          onDropdownChange={handleDropdownChange}
          onSearchChange={handleSearchChange}
          onToggleChange={handleToggleChange}
          searchValue={searchValue}
          toggleFilter={toggleFilter}
        />
        <ProjectRequestsStatistics
          onFilterClick={handleDropdownChange}
          projectId={numericProjectId}
        />
      </div>

      {!isDesigner && (
        <ProjectRequestsBulkActions
          isLoading={isBulkLoading}
          onCopyLinks={handleBulkCopyLinks}
          onStatusSelect={handleStatusSelect}
          selectedCount={selectedCount}
        />
      )}

      {isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
          <p className="text-sm text-red-800">
            Failed to load requests. Please try again.
          </p>
          <button
            className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
            onClick={() => refetch()}
            type="button"
          >
            Retry
          </button>
        </div>
      )}

      <ProjectRequestsTable
        data={requestsData?.requests || []}
        dropdownFilter={dropdownFilter}
        isDesigner={isDesigner}
        isLoading={isLoading}
        itemsPerPage={itemsPerPage}
        onClearSelection={isDesigner ? undefined : handleClearSelection}
        onItemsPerPageChange={handleItemsPerPageChange}
        onPageChange={handlePageChange}
        onSelectAll={isDesigner ? undefined : handleSelectAll}
        onSelectionChange={isDesigner ? undefined : handleSelectionChange}
        onSortChange={handleSortChange}
        page={page}
        selectedItems={isDesigner ? undefined : selectedItems}
        sortDescriptor={sortDescriptor}
        totalItems={requestsData?.total ?? cachedTotalItems}
        totalPages={
          requestsData?.pagination?.totalPages ??
          (requestsData?.total == null
            ? cachedTotalPages
            : Math.ceil(requestsData.total / itemsPerPage))
        }
      />

      {pendingStatusChange && (
        <BulkChangeStatusModal
          isLoading={bulkChangeStatusMutation.isPending}
          isOpen={true}
          newStatusLabel={pendingStatusChange.statusLabel}
          newStatusVariant={pendingStatusChange.statusVariant}
          onConfirm={confirmStatusChange}
          onOpenChange={(open) => {
            if (!open) setPendingStatusChange(null);
          }}
          selectedRequests={pendingStatusChange.selectedRequests}
        />
      )}
    </div>
  );
}
